<template>
  <div class="modal-card" style="width: 1000px; max-width: 95vw;">
    <header class="modal-card-head">
      <p class="modal-card-title">
        {{ isEdit ? 'Actualizar' : 'Configurar' }} Comisión Precalificadora
      </p>
    </header>
      
    <section class="modal-card-body">
      <!-- Información del departamento -->
      <div class="notification is-primary is-light mb-5">
        <div class="columns is-vcentered">
          <div class="column">
            <p class="has-text-weight-bold is-size-5 mb-1">
              {{ department.unit_name }}
            </p>
            <p class="is-size-6">
              Proceso de Evaluación {{ year }} • Tipo: {{ department.unit_type }}
            </p>
          </div>
        </div>
      </div>

      <!-- Información importante -->
      <div class="notification is-info is-light mb-4">
        <strong>Estructura de la Comisión:</strong>
        <ul class="ml-5 mt-2">
          <li><strong>Miembro 1:</strong> Director del departamento (se asigna automáticamente)</li>
          <li><strong>Miembro 2:</strong> Académico del mismo departamento</li>
          <li><strong>Miembro 3:</strong> Académico de otro departamento de la FING (o fuera de ella)</li>
        </ul>
      </div>

      <div class="columns">
        <!-- Formularios de miembros -->
        <div class="column is-8">
          <div class="columns is-multiline">
            <!-- Miembro 1 - Director (Auto-asignado) -->
            <div class="column is-12">
              <div class="box member-box director-box">
                <div class="has-text-centered mb-3">
                  <p class="has-text-weight-bold is-size-5 mt-2">Miembro 1 - Director</p>
                  <p class="is-size-7 has-text-grey">Se asigna automáticamente según nombramiento</p>
                </div>
                
                <div v-if="directorInfo" class="content">
                  <p class="mb-1">
                    <strong>{{ directorInfo.name }}</strong>
                  </p>
                  <p class="mb-0">
                    {{ directorInfo.email }}
                  </p>
                </div>
                <div v-else class="notification is-warning is-light">
                  No se encontró director activo para este departamento. Se asignará automáticamente al crear la comisión.
                </div>
              </div>
            </div>

            <!-- Miembro 2 - Mismo Departamento -->
            <div class="column is-6">
              <div class="box member-box">
                <div class="has-text-centered mb-3">
                  <p class="has-text-weight-bold is-size-5 mt-2">Miembro 2</p>
                  <p class="is-size-7 has-text-grey">Del mismo departamento</p>
                </div>
                
                <b-field 
                  label="Nombre Completo" 
                  :type="errors.member_2_name ? 'is-danger' : ''" 
                  :message="errors.member_2_name"
                >
                  <b-input
                    v-model="formData.member_2_name"
                    placeholder="Nombre completo"
                    @input="clearError('member_2_name'); validateDuplicates()"
                    :disabled="isSaving"
                  ></b-input>
                </b-field>

                <b-field 
                  label="Email Institucional" 
                  :type="errors.member_2_email ? 'is-danger' : ''" 
                  :message="errors.member_2_email"
                >
                  <b-input
                    v-model="formData.member_2_email"
                    type="email"
                    placeholder="correo@usach.cl"
                    @input="validateEmail('member_2_email'); validateDuplicates()"
                    :disabled="isSaving"
                  ></b-input>
                </b-field>

                <b-button 
                  v-if="formData.member_2_email"
                  size="is-small" 
                  type="is-light"
                  @click="clearMember('member_2')"
                  expanded
                  :disabled="isSaving"
                >
                  Limpiar
                </b-button>

                <b-button
                  size="is-small"
                  type="is-info"
                  @click="openUserSearch('member_2')"
                  expanded
                  class="mt-2"
                  :disabled="isSaving"
                >
                  Buscar en el Departamento
                </b-button>
              </div>
            </div>

            <!-- Miembro 3 - Externo -->
            <div class="column is-6">
              <div class="box member-box">
                <div class="has-text-centered mb-3">
                  <p class="has-text-weight-bold is-size-5 mt-2">Miembro 3</p>
                  <p class="is-size-7 has-text-grey">De otro departamento</p>
                </div>
                
                <b-field 
                  label="Nombre Completo" 
                  :type="errors.member_3_name ? 'is-danger' : ''" 
                  :message="errors.member_3_name"
                >
                  <b-input
                    v-model="formData.member_3_name"
                    placeholder="Nombre completo"
                    @input="clearError('member_3_name'); validateDuplicates()"
                    :disabled="isSaving"
                  ></b-input>
                </b-field>

                <b-field 
                  label="Email Institucional" 
                  :type="errors.member_3_email ? 'is-danger' : ''" 
                  :message="errors.member_3_email"
                >
                  <b-input
                    v-model="formData.member_3_email"
                    type="email"
                    placeholder="correo@usach.cl"
                    @input="validateEmail('member_3_email'); validateDuplicates()"
                    :disabled="isSaving"
                  ></b-input>
                </b-field>

                <b-button 
                  v-if="formData.member_3_email"
                  size="is-small" 
                  type="is-light"
                  @click="clearMember('member_3')"
                  expanded
                  :disabled="isSaving"
                >
                  Limpiar
                </b-button>

                <b-button
                  size="is-small"
                  type="is-link"
                  @click="openUserSearch('member_3')"
                  expanded
                  class="mt-2"
                  :disabled="isSaving"
                >
                  Buscar en Otros Departamentos
                </b-button>
              </div>
            </div>
          </div>

          <!-- Advertencias -->
          <div v-if="duplicateWarning" class="notification is-warning">
            <strong>Atención:</strong> Los 3 miembros de la comisión deben ser personas diferentes.
          </div>

          <div v-if="Object.keys(errors).length > 0" class="notification is-danger is-light">
            Por favor corrija los errores antes de continuar.
          </div>
        </div>

        <!-- Panel lateral con información -->
        <div class="column is-4">
          <div class="box" style="background: #fafafa; position: sticky; top: 20px;">
            <p class="has-text-weight-bold mb-3">
              Ayuda
            </p>
            
            <div class="content is-small">
              <p><strong>¿Cómo configurar la comisión?</strong></p>
              <ol>
                <li>El <strong>Miembro 1 (Director)</strong> se asigna automáticamente</li>
                <li>Seleccione el <strong>Miembro 2</strong> del mismo departamento usando el botón de búsqueda</li>
                <li>Seleccione el <strong>Miembro 3</strong> de otro departamento usando el botón de búsqueda</li>
                <li>Verifique que los emails sean diferentes</li>
                <li>Haga clic en "Guardar Comisión"</li>
              </ol>

              <hr>

              <p><strong>Roles de la Comisión:</strong></p>
              <ul>
                <li>Revisión de precalificaciones</li>
                <li>Firma digital de documentos</li>
                <li>Validación de puntajes</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer class="modal-card-foot is-justify-content-space-between">
      <div>
        <p class="is-size-7 has-text-grey">
          Los miembros recibirán códigos de verificación para firmar digitalmente
        </p>
      </div>
      <div>
        <b-button @click="close" :disabled="isSaving">
          Cancelar
        </b-button>
        <b-button
          type="is-primary"
          @click="save"
          :loading="isSaving"
          :disabled="!isFormValid"
        >
          <span>{{ isEdit ? 'Actualizar Comisión' : 'Guardar Comisión' }}</span>
        </b-button>
      </div>
    </footer>

    <!-- Modal de búsqueda de usuarios -->
    <b-modal v-model="showUserSearchModal" :width="900" scroll="keep">
      <div class="box search-modal-box">
        <div class="modal-header-section mb-5">
          <p class="title is-4 mb-2">
            <span class="ml-2">Buscar {{ searchMemberType === 'member_2' ? 'Miembro 2 (Mismo Depto)' : 'Miembro 3 (Otro Depto)' }}</span>
          </p>
          <p class="subtitle is-6 has-text-grey">
            Seleccione un usuario disponible de la lista
          </p>
        </div>

        <!-- Sección de búsqueda principal -->
        <div class="search-section mb-4">
          <b-field label="Búsqueda por nombre o email" label-position="on-border">
            <b-input 
              placeholder="Escriba para buscar..." 
              v-model="userSearchQuery" 
              @input="debouncedSearchUsers" 
              size="is-medium"
            ></b-input>
          </b-field>
        </div>

        <!-- Sección de filtros -->
        <div class="filters-section mb-5">
          <p class="has-text-weight-semibold mb-3">
            <span class="ml-1">Filtros</span>
          </p>
          <div class="columns">
            <div class="column is-6">
              <b-field label="Estado" label-position="on-border">
                <b-select v-model="assignmentFilter" @input="applyFilters" expanded size="is-medium">
                  <option value="all">Todos los estados</option>
                  <option value="available">Solo disponibles</option>
                  <option value="assigned">Solo asignados</option>
                  <option value="directors">Solo directores</option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-6">
              <b-field label="Departamento" label-position="on-border">
                <b-autocomplete
                  v-model="departmentFilter"
                  :data="filteredDepartments"
                  placeholder="Seleccionar departamento..."
                  field="name"
                  size="is-medium"
                  @select="option => { selectedDepartment = option ? option.id : null; applyFilters(); }"
                  clearable
                  open-on-focus
                  :loading="isLoadingDepartments"
              >
                <template slot-scope="props">
                  <div class="media">
                    <div class="media-content">
                      {{ props.option.name }}
                    </div>
                  </div>
                </template>
                <template #empty>No se encontraron departamentos</template>
              </b-autocomplete>
            </b-field>
          </div>
        </div>
      </div>

        <div v-if="isLoadingUsers" class="has-text-centered py-5">
          <b-loading :is-full-page="false" :active="true"></b-loading>
        </div>

        <div v-else-if="searchResults.length === 0" class="notification is-light">
          No se encontraron usuarios.
        </div>

        <div v-else>
          <!-- Tabla con usuarios -->
          <b-table
            :data="searchResults"
            :mobile-cards="false"
            :hoverable="true"
            :narrowed="true"
            class="mt-4"
          >
            <b-table-column field="name" label="Nombre" v-slot="props">
              <div>
                <p class="has-text-weight-bold">
                  {{ props.row.name }}
                </p>
                <p class="is-size-7 has-text-grey">
                  {{ props.row.email }}
                </p>
              </div>
            </b-table-column>

            <b-table-column field="department_names" label="Departamento" v-slot="props">
              <p v-if="props.row.department_names" class="is-size-7 has-text-info">
                {{ props.row.department_names }}
              </p>
              <p v-else class="is-size-7 has-text-grey-light">
                Sin departamento
              </p>
            </b-table-column>

            <b-table-column field="status" label="Estado" width="180" v-slot="props">
              <div>
                <b-tag v-if="props.row.is_director" type="is-danger">
                  <span class="ml-1">Director</span>
                </b-tag>
                <b-tag v-else-if="props.row.assigned_committee" type="is-danger">
                  <span class="ml-1">Asignado</span>
                </b-tag>
                <b-tag v-else-if="props.row.is_in_committee" type="is-warning">
                  <span class="ml-1">En comisión</span>
                </b-tag>
                <b-tag v-else type="is-success">
                  <span class="ml-1">Disponible</span>
                </b-tag>
              </div>
              <p v-if="props.row.assigned_committee" class="is-size-7 has-text-grey mt-1">
                {{ props.row.assigned_committee.unit_name }}
              </p>
            </b-table-column>

            <b-table-column field="actions" label="Acción" width="120" v-slot="props">
              <b-button
                v-if="!props.row.is_director && !props.row.assigned_committee && !props.row.is_in_committee"
                type="is-primary"
                size="is-small"
                icon-left="check"
                @click="selectUser(props.row)"
              >
                Seleccionar
              </b-button>
              <span v-else class="has-text-grey-light is-size-7">
                No disponible
              </span>
            </b-table-column>

            <template #empty>
              <div class="has-text-centered py-4">
                <p class="has-text-grey">No hay usuarios que mostrar</p>
              </div>
            </template>
          </b-table>

          <!-- Paginación simple -->
          <div v-if="totalPages > 1" class="has-text-centered mt-4">
            <b-button
              :disabled="currentPage === 1"
              @click="onPageChange(currentPage - 1)"
              icon-left="chevron-left"
              size="is-small"
            >
              Anterior
            </b-button>
            <span class="mx-3 has-text-grey">
              Página {{ currentPage }} de {{ totalPages }} ({{ totalUsers }} usuarios)
            </span>
            <b-button
              :disabled="currentPage === totalPages"
              @click="onPageChange(currentPage + 1)"
              icon-right="chevron-right"
              size="is-small"
            >
              Siguiente
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CommitteeConfigModal',

  props: {
    department: {
      type: Object,
      required: true
    },
    year: {
      type: Number,
      required: true
    },
    existingCommittee: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      isEdit: false,
      committeeId: null,
      formData: {
        member_2_name: '',
        member_2_email: '',
        member_3_name: '',
        member_3_email: ''
      },
      directorInfo: null,
      errors: {},
      isSaving: false,
      duplicateWarning: false,
      
      // User search modal
      showUserSearchModal: false,
      searchMemberType: null, // 'member_2' o 'member_3'
      userSearchQuery: '',
      searchResults: [],
      isLoadingUsers: false,
      
      // Filtros de búsqueda
      assignmentFilter: 'all', // 'all', 'available', 'assigned', 'directors'
      departmentFilter: '',
      
      // Paginación
      currentPage: 1,
      perPage: 15,
      totalUsers: 0,
      totalPages: 0,
      
      // Departamentos para autocomplete
      departments: [],
      selectedDepartment: null,
      isLoadingDepartments: false
    };
  },

  computed: {
    isFormValid() {
      return (
        this.formData.member_2_name.trim() !== '' &&
        this.formData.member_2_email.trim() !== '' &&
        this.formData.member_3_name.trim() !== '' &&
        this.formData.member_3_email.trim() !== '' &&
        this.isEmailValid(this.formData.member_2_email) &&
        this.isEmailValid(this.formData.member_3_email) &&
        !this.duplicateWarning &&
        Object.keys(this.errors).length === 0
      );
    },
    
    filteredDepartments() {
      if (!this.departmentFilter) {
        return this.departments;
      }
      const filter = this.departmentFilter.toLowerCase();
      return this.departments.filter(dept => 
        dept.name.toLowerCase().includes(filter)
      );
    }
  },

  created() {
    this.debouncedSearchUsers = this.debounce(() => { this.searchUsers(); }, 400);
  },

  mounted() {
    this.loadExistingData();
    // Si es nuevo comité (no edit), cargar info del director
    if (!this.isEdit) {
      this.loadDirectorInfo();
    }
  },

  methods: {
    debounce(func, wait = 300) {
      let timeout;
      return function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    },

    async loadDirectorInfo() {
      try {
        const response = await axios.get(`/committee_members/show_by_unit/${this.department.unit_id}`, {
          params: { year: this.year }
        });
        
        if (response.data.director_info) {
          this.directorInfo = response.data.director_info;
        }
      } catch (error) {
        console.log('No se pudo cargar info del director (podría no estar configurado aún)');
      }
    },

    loadExistingData() {
      if (this.existingCommittee) {
        this.isEdit = true;
        this.committeeId = this.existingCommittee.id;
        
        // Cargar director
        if (this.existingCommittee.director) {
          this.directorInfo = this.existingCommittee.director;
        }

        // Cargar member_2 y member_3
        if (this.existingCommittee.member_2) {
          this.formData.member_2_name = this.existingCommittee.member_2.name || '';
          this.formData.member_2_email = this.existingCommittee.member_2.email || '';
        }

        if (this.existingCommittee.member_3) {
          this.formData.member_3_name = this.existingCommittee.member_3.name || '';
          this.formData.member_3_email = this.existingCommittee.member_3.email || '';
        }
      }
    },

    openUserSearch(memberType) {
      this.searchMemberType = memberType;
      this.userSearchQuery = '';
      this.searchResults = [];
      this.assignmentFilter = 'available'; // Por defecto mostrar solo disponibles
      this.selectedDepartment = null;
      this.departmentFilter = '';
      this.currentPage = 1;
      this.showUserSearchModal = true;
      this.loadDepartments();
      this.searchUsers();
    },

    async loadDepartments() {
      this.isLoadingDepartments = true;
      try {
        const response = await axios.get('/committee_members/departments');
        if (response.data.success) {
          this.departments = response.data.departments;
        }
      } catch (error) {
        console.error('Error loading departments:', error);
      } finally {
        this.isLoadingDepartments = false;
      }
    },

    applyFilters() {
      this.currentPage = 1;
      this.searchUsers();
    },
    
    onPageChange(page) {
      this.currentPage = page;
      this.searchUsers();
    },
    
    getDepartmentName(departmentId) {
      const dept = this.departments.find(d => d.id === departmentId);
      return dept ? dept.name : '';
    },

    async searchUsers() {
      this.isLoadingUsers = true;
      try {
        const params = {
          unit_id: this.department.unit_id,
          member_type: this.searchMemberType === 'member_2' ? 'same_department' : 'external',
          query: this.userSearchQuery,
          process_year: this.year,
          page: this.currentPage,
          per_page: this.perPage,
          status_filter: this.assignmentFilter
        };
        
        // Agregar filtro de departamento si está seleccionado
        if (this.selectedDepartment) {
          params.department_id = this.selectedDepartment;
        }
        
        const response = await axios.get('/committee_members/available_users', { params });

        if (response.data.success) {
          this.searchResults = response.data.users || [];
          if (response.data.pagination) {
            this.totalUsers = response.data.pagination.total_users;
            this.totalPages = response.data.pagination.total_pages;
            this.currentPage = response.data.pagination.current_page;
          }
        }
      } catch (error) {
        console.error('Error searching users:', error);
        this.$buefy.toast.open({
          message: 'Error al buscar usuarios',
          type: 'is-danger',
          duration: 3000
        });
      } finally {
        this.isLoadingUsers = false;
      }
    },

    selectUser(user) {
      // Verificar si es director de departamento
      if (user.is_director) {
        this.$buefy.toast.open({
          message: 'Los directores de departamento solo pueden ser miembro 1 de su propio departamento',
          type: 'is-danger',
          duration: 4000
        });
        return;
      }

      // Verificar si ya está en otro comité
      if (user.assigned_committee) {
        this.$buefy.toast.open({
          message: `Este usuario ya está asignado a la comisión de ${user.assigned_committee.unit_name}`,
          type: 'is-danger',
          duration: 4000
        });
        return;
      }

      // Verificar si ya está en el comité actual
      if (user.is_in_committee) {
        this.$buefy.toast.open({
          message: 'Este usuario ya está asignado en la comisión',
          type: 'is-warning',
          duration: 3000
        });
        return;
      }

      if (this.searchMemberType === 'member_2') {
        this.formData.member_2_name = user.name;
        this.formData.member_2_email = user.email;
        this.clearError('member_2_name');
        this.clearError('member_2_email');
      } else if (this.searchMemberType === 'member_3') {
        this.formData.member_3_name = user.name;
        this.formData.member_3_email = user.email;
        this.clearError('member_3_name');
        this.clearError('member_3_email');
      }

      this.validateDuplicates();
      this.showUserSearchModal = false;

      this.$buefy.toast.open({
        message: `${user.name} seleccionado correctamente`,
        type: 'is-success',
        duration: 2000
      });
    },

    clearMember(memberType) {
      if (memberType === 'member_2') {
        this.formData.member_2_name = '';
        this.formData.member_2_email = '';
        this.clearError('member_2_name');
        this.clearError('member_2_email');
      } else if (memberType === 'member_3') {
        this.formData.member_3_name = '';
        this.formData.member_3_email = '';
        this.clearError('member_3_name');
        this.clearError('member_3_email');
      }
      this.validateDuplicates();
    },

    isUserInCommittee(user) {
      const email = user.email.toLowerCase();
      const directorEmail = this.directorInfo && this.directorInfo.email ? this.directorInfo.email.toLowerCase() : null;
      return (
        email === directorEmail ||
        email === this.formData.member_2_email.toLowerCase() ||
        email === this.formData.member_3_email.toLowerCase()
      );
    },

    validateDuplicates() {
      const directorEmail = this.directorInfo && this.directorInfo.email ? this.directorInfo.email.toLowerCase() : null;
      const emails = [
        directorEmail,
        this.formData.member_2_email.trim().toLowerCase(),
        this.formData.member_3_email.trim().toLowerCase()
      ].filter(e => e);

      const uniqueEmails = new Set(emails);
      this.duplicateWarning = emails.length !== uniqueEmails.size;
    },

    close() {
      this.$parent.close();
    },

    isEmailValid(email) {
      const regex = /^[^\s@]+@usach\.cl$/i;
      return regex.test(email);
    },

    validateEmail(field) {
      this.clearError(field);
      
      const email = this.formData[field].trim().toLowerCase();
      
      if (!email) {
        this.errors[field] = 'El email es requerido';
        return;
      }

      if (!this.isEmailValid(email)) {
        this.errors[field] = 'Debe ser un email válido @usach.cl';
        return;
      }

      this.$forceUpdate();
    },

    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
        this.$forceUpdate();
      }
    },

    validateForm() {
      this.errors = {};

      // Validar nombres
      if (!this.formData.member_2_name.trim()) {
        this.errors.member_2_name = 'El nombre es requerido';
      }
      if (!this.formData.member_3_name.trim()) {
        this.errors.member_3_name = 'El nombre es requerido';
      }

      // Validar emails
      this.validateEmail('member_2_email');
      this.validateEmail('member_3_email');

      // Verificar duplicados
      this.validateDuplicates();

      return Object.keys(this.errors).length === 0 && !this.duplicateWarning;
    },

    async save() {
      if (!this.validateForm()) {
        this.$buefy.toast.open({
          message: 'Por favor corrija los errores del formulario',
          type: 'is-warning',
          duration: 3000
        });
        return;
      }

      this.isSaving = true;

      try {
        const endpoint = this.isEdit
          ? `/committee_members/${this.committeeId}`
          : '/committee_members';

        const method = this.isEdit ? 'put' : 'post';

        const payload = {
          unit_id: this.department.unit_id,
          member_2_name: this.formData.member_2_name.trim(),
          member_2_email: this.formData.member_2_email.trim().toLowerCase(),
          member_3_name: this.formData.member_3_name.trim(),
          member_3_email: this.formData.member_3_email.trim().toLowerCase(),
          process_year: this.year
        };

        const response = await axios[method](endpoint, payload);

        if (response.data.success) {
          this.$buefy.toast.open({
            message: response.data.message || 'Comisión configurada correctamente',
            type: 'is-success',
            duration: 3000
          });

          this.$emit('committee-saved', response.data.committee);
          this.close();
        } else {
          throw new Error(response.data.error || 'Error al guardar la comisión');
        }
      } catch (error) {
        console.error('Error saving committee:', error);
        const errorMessage = error.response && error.response.data && error.response.data.error 
          ? error.response.data.error 
          : 'Error al configurar la comisión';
        this.$buefy.toast.open({
          message: errorMessage,
          type: 'is-danger',
          duration: 5000
        });
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.member-box {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
  height: 100%;
}

.member-box:hover {
  border-color: #3273dc;
  box-shadow: 0 4px 12px rgba(50, 115, 220, 0.1);
}

.director-box {
  background: linear-gradient(135deg, #e8f4fd 0%, #d4ebf7 100%);
  border-color: #3273dc;
}

/* Estilos para el modal de búsqueda */
.search-modal-box {
  padding: 2rem;
  border-radius: 8px;
}

.modal-header-section {
  border-bottom: 2px solid #f5f5f5;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
}

.modal-header-section .title {
  margin-bottom: 0.75rem !important;
  line-height: 1.3;
}

.modal-header-section .subtitle {
  margin-top: 0.5rem;
  line-height: 1.4;
}

.search-section {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.filters-section {
  background: #fafafa;
  padding: 1.5rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.filters-section .columns {
  margin-bottom: 0;
}

.filters-section .column {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
</style>
